import maplibregl, { type Map as MapLibreMap, type Marker } from "maplibre-gl";
import { VECTOR_STYLE_URL } from "../../shared/basemap.ts";
import { whenMapHasStyle } from "../../shared/map-style-safe.ts";
import type { LngLat } from "../../shared/types.ts";
import { el } from "./dom.ts";

/** Read-only embedded map with a single marker (spot preview / public page). */
export class SpotLocationEmbed {
  private map: MapLibreMap;
  private marker: Marker;
  private destroyed = false;
  private resizeObserver: ResizeObserver;

  constructor(container: HTMLElement, point: LngLat) {
    container.replaceChildren();
    const mapEl = el("div", "qp-spot-loc-map");
    container.append(mapEl);
    this.map = new maplibregl.Map({
      container: mapEl,
      style: VECTOR_STYLE_URL,
      center: point,
      zoom: 15,
      attributionControl: false,
      cooperativeGestures: true,
      pitchWithRotate: false,
      dragRotate: false,
    });
    this.marker = new maplibregl.Marker({ color: "#8b5cf6" }).setLngLat(point).addTo(this.map);
    whenMapHasStyle(this.map, () => {
      this.map.jumpTo({ center: point, zoom: 15 });
    });
    this.resizeObserver = new ResizeObserver(() => {
      if (this.destroyed) return;
      this.map.resize();
    });
    this.resizeObserver.observe(mapEl);
    requestAnimationFrame(() => this.map.resize());
  }

  setPoint(point: LngLat): void {
    if (this.destroyed) return;
    this.marker.setLngLat(point);
    this.map.jumpTo({ center: point });
  }

  resize(): void {
    if (this.destroyed) return;
    this.map.resize();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.resizeObserver.disconnect();
    this.marker.remove();
    this.map.remove();
  }
}
